import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { handleFetchError, translate } from '../../utils';
import useGlobalSpinner from '../../hooks/useGlobalSpinner';
import FilterSelection from '../FilterSelection/FilterSelection';
import { PortfolioDashboardProvider } from './PortfolioDashboardContext';
import { PortfolioDatePicker } from './PortfolioDatePicker';
import { PortfolioNotices } from './PortfolioNotices';
import { PortfolioOverview } from './PortfolioOverview';
import { PortfolioHotelRanking } from './PortfolioHotelRanking';
import { PortfolioMetrics } from './PortfolioMetrics';
import { PortfolioSummary } from './PortfolioSummary';
import { buildSearchParams } from './portfolioDashboardUtilities';

const PROPERTY_FILTER_SUGGESTED_FIELD_NAMES = ['properties'];

/** @param {import('./portfolioDashboard.types').PortfolioDashboardProps} props */
const PortfolioDashboard = function PortfolioDashboard({
    propertiesOptionsForFilter,
    selectedPropertiesKeys: initialSelectedPropertiesKeys,
    selectedPeriod: initialPeriod,
    selectedOverviewMetric: initialOverviewMetric,
    portfolioData: initialPortfolioData,
    currency,
}) {
    const [period] = useState(initialPeriod);
    const [selectedDate, setSelectedDate] = useState(initialPortfolioData.overview.selectedDate);
    const [overviewMetric, setOverviewMetric] = useState(initialOverviewMetric);
    const [propertyFilter, setPropertyFilter] = useState(
        initialSelectedPropertiesKeys === null ? {} : { properties: { in: initialSelectedPropertiesKeys } },
    );
    const [portfolioData, setPortfolioData] = useState(initialPortfolioData);
    const [, setIsLoading] = useGlobalSpinner(false);
    const abortControllerRef = useRef(null);

    const today = portfolioData.overview.today;

    const propertyFilterSchema = useMemo(() => ({
        properties: {
            label: translate('frontend', 'PORTFOLIO_DASHBOARD_PROPERTIES'),
            type: 'enum',
            values: Object.fromEntries(
                Object.entries(propertiesOptionsForFilter).map(([propertyKey, name]) => [propertyKey, { label: name }]),
            ),
            displayValue: (selectedPropertiesKeys) => translate('frontend', 'FILTER_SELECTION_SELECTED_VALUES', {
                count: selectedPropertiesKeys.length,
            }),
        },
    }), [propertiesOptionsForFilter]);

    const loadPortfolioData = useCallback(async (selectedPropertiesKeys, selectedPeriod, selectedDate, selectedOverviewMetric) => {
        const queryString = buildSearchParams(selectedPropertiesKeys, selectedPeriod, selectedDate, today, selectedOverviewMetric).toString();

        replacePageQueryString(queryString);

        setIsLoading(true);

        try {
            const response = await fetch(`/portfolio-dashboard/data?${queryString}`, { signal: abortControllerRef.current.signal });

            if (response.ok) {
                setPortfolioData(await response.json());
            } else {
                await handleFetchError(response);
            }
        } catch (error) {
            if (error.name !== 'AbortError') alert(translate('frontend', 'ERROR_INPROCESS'));
        } finally {
            setIsLoading(false);
        }
    }, [setIsLoading, today]);

    const handleSelectedDateChange = useCallback((newSelectedDate) => {
        if (newSelectedDate === selectedDate) return;

        setSelectedDate(newSelectedDate);
        loadPortfolioData(propertyFilter.properties?.in, period, newSelectedDate, overviewMetric);
    }, [loadPortfolioData, propertyFilter, period, selectedDate, overviewMetric]);

    const handlePropertyFilterChange = useCallback((newFilter) => {
        setPropertyFilter(newFilter);
        loadPortfolioData(newFilter.properties?.in, period, selectedDate, overviewMetric);
    }, [loadPortfolioData, period, selectedDate, overviewMetric]);

    const handleOverviewMetricChange = useCallback((newOverviewMetric) => {
        setOverviewMetric(newOverviewMetric);
        replacePageQueryString(
            buildSearchParams(propertyFilter.properties?.in, period, selectedDate, today, newOverviewMetric).toString(),
        );
    }, [propertyFilter, period, selectedDate, today]);

    useEffect(() => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        return () => abortController.abort();
    }, []);

    return (
        <>
            <section className="content-header flex justify-between items-center gap-3">
                <h1>{translate('frontend', 'PORTFOLIO_DASHBOARD')}</h1>

                <div>
                    <FilterSelection
                        schema={propertyFilterSchema}
                        filter={propertyFilter}
                        suggestedFieldNames={PROPERTY_FILTER_SUGGESTED_FIELD_NAMES}
                        applyFilters={handlePropertyFilterChange}
                        clearBtn={false}
                        appearance="defaultClear"
                    />
                </div>
            </section>

            <section className="content">
                <PortfolioDashboardProvider period={period} isTodaySelected={portfolioData.overview.selectedDate === today}>
                    <PortfolioSummary portfolioSummary={portfolioData.portfolioSummary} />

                    <PortfolioDatePicker
                        selectedDate={selectedDate}
                        onChange={handleSelectedDateChange}
                    />

                    <div className="grid mb-3">
                        <div className="g-col-12 g-col-lg-6 g-col-xl-9">
                            <PortfolioOverview
                                overview={portfolioData.overview}
                                metricKey={overviewMetric}
                                onMetricChange={handleOverviewMetricChange}
                            />
                        </div>

                        <div className="g-col-12 g-col-lg-6 g-col-xl-3">
                            <PortfolioNotices notices={portfolioData.notices} />
                        </div>
                    </div>

                    <PortfolioMetrics
                        portfolioMetrics={portfolioData.portfolioMetrics}
                        currency={currency}
                    />

                    <PortfolioHotelRanking
                        hotelRanking={portfolioData.hotelRanking}
                        currency={currency}
                    />
                </PortfolioDashboardProvider>
            </section>
        </>
    );
};

/** @param {string} queryString */
function replacePageQueryString(queryString) {
    window.history.replaceState(null, '', queryString === '' ? window.location.pathname : `${window.location.pathname}?${queryString}`);
}

export default PortfolioDashboard;
